/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import { bool, object } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import mbpLogger from '@module-logger/mbp';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router';
import { format } from 'date-fns';

import { getLastAddedOrderItemDetails } from '../../../../state/ducks/AddToCart/AddToCart-Selectors';
import { getUserSubmittedProductFilterZipcode, getWrapUpProductRefTitle } from '../../../../state/ducks/App/App-Selectors';
import { getBrand, getBrandName } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { triggerInterstitialLogin } from '../../../../state/ducks/Member/ducks/Auth/Auth-Actions';
import { getOrderId, getCartCount, getOrderContainsSubscriptionItem } from '../../../../state/ducks/Common/Common-Selectors';
import { getIsAuthenticatedStatus } from '../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

import findWrapUpsAndContent from '../../../gql/queries/findWrapUpsAndContent';
import findAddOnsAndContent from '../../../gql/queries/findAddOnsAndContent';

import AddOnsPageBuilder from './AddOnsPageBuilder';
import { GRAPHQL_ENV } from '../../../gql';
import { getCartAttributeByNameAndReturnValue } from '../../../helpers/page/pageHelpers';
import { getFeatureFlags } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import SiteLoader from '../../../pages/Checkout/containers/Common/SiteLoader/SiteLoader';
import GraphqlSeoHeadTags from '../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';

// eslint-disable-next-line no-unused-vars
const AddOnsContentContainer = ({ page, isABFallback }) => {
    const brand = useSelector(getBrand);
    const brandName = useSelector(getBrandName);
    const cartCount = useSelector(getCartCount);
    const featureFlags = useSelector(getFeatureFlags);
    const isAuthenticated = useSelector(getIsAuthenticatedStatus);
    const orderId = useSelector(getOrderId);
    const orderContainsSubscriptionItem = useSelector(getOrderContainsSubscriptionItem);
    const history = useHistory();
    const dispatch = useDispatch();

    const forceAddonWrapUpRedesignFlag = featureFlags['is-force-add-on-and-wrap-up-redesign-enabled'];
    const wrapupAddonRedesign = forceAddonWrapUpRedesignFlag;
    const orderZip = useSelector(getUserSubmittedProductFilterZipcode);

    const location = useLocation();
    const lastAddedOrderItem = useSelector(getLastAddedOrderItemDetails);

    let dateToFormat = lastAddedOrderItem?.deliveryInfo?.deliveryDate;
    const productCode = lastAddedOrderItem?.product?.productCode;

    if (typeof dateToFormat === 'string') {
        // date can come in as either a string or an actual date obj.
        // if it's a string, need to change date format to use / instead of - to be properly parsed in all browsers
        dateToFormat = dateToFormat.split('-').join('/');
    }
    const deliveryDate = dateToFormat ? format(new Date(dateToFormat), 'MM/dd/yyyy') : '';

    const categoryId = getCartAttributeByNameAndReturnValue(lastAddedOrderItem?.attributes || [], 'categoryId');
    const parentSkuCode = lastAddedOrderItem?.product?.parentProductCode || '';

    const cartItemProduct = lastAddedOrderItem?.product || {};
    const sympathyValue = cartItemProduct?.sympathyValue === '1' || cartItemProduct?.sympathyValue === '2';

    useEffect(() => {
        if (isABFallback) {
            dispatch(trackEvent({
                eventCategory: 'Personalized addons',
                eventAction: 'Impression',
                eventLabel: 'Variant/Fallback',
            }));
        }
    });

    const redirectToShipping = () => {
        if (orderContainsSubscriptionItem.length > 0 && !isAuthenticated) {
            dispatch(triggerInterstitialLogin(history, true));
        } else if ((featureFlags['is-interstitial-login-enabled'] || featureFlags['is-native-interstitial-page-enabled']) && !isAuthenticated) {
            dispatch(triggerInterstitialLogin(history));
        } else if (orderId !== '') {
            // If user has not selected any addons go straight to cart
            if (cartCount > 1) {
                history.push(`/checkout/cart/${orderId}`);
            } else {
                history.push(`/checkout/shipping/${orderId}`);
            }
        } else if (!orderId) {
            // If someone is directly linked to /add-ons and clicks continue without anything in cart
            // they are redirected to the homepage instead of an empty cart
            history.push('/');
        }
    };

    if (lastAddedOrderItem && Object.keys(lastAddedOrderItem).length === 0) {
        redirectToShipping();
        return null;
    }

    const ADD_ON_AND_CONTENT_QUERY = findAddOnsAndContent();
    const wrapUpRefTitle = useSelector(getWrapUpProductRefTitle);
    const WRAP_UP_AND_CONTENT_QUERY = findWrapUpsAndContent(brand, wrapUpRefTitle);

    const { loading: addOnLoading, error: addOnError, data: addOnData } = useQuery(ADD_ON_AND_CONTENT_QUERY, {
        variables: {
            brand: brandName,
            locale: 'en-us',
            environment: GRAPHQL_ENV,
            zipCode: orderZip,
            deliveryDate,
            categoryId,
            productBaseCode: parentSkuCode,
            productSku: productCode,
            query: '',
        },
    });

    const { loading: wrapUpLoading, error: wrapUpError, data: wrapUpData } = useQuery(WRAP_UP_AND_CONTENT_QUERY, {
        variables: {
            environment: GRAPHQL_ENV,
            key: '',
        },
    });

    if (addOnLoading || wrapUpLoading) {
        return <SiteLoader />;
    }

    if (addOnError || wrapUpError) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: ADD_ON_AND_CONTENT_QUERY,
            jsError: addOnError,
            component: 'AddOnsContentContainer.js',
            message: `Failed to fetch content data for /add-ons on brand ${brandName}`,
        });
        redirectToShipping();
        return null;
    }

    const { findAddonsAndContent: addOnDataFromCombinedQuery } = addOnData;
    const { findWrapUpsAndContent: wrapUpDataFromCombinedQuery } = wrapUpData;

    // 4701; see lines around 106 in GraphqlAddOnsContainer.js for reference. the props and variables in this flow are confusing, refer to this guide when troublshooting or data hunting

    const content = addOnDataFromCombinedQuery?.addonGroups || [];
    // pim data; old query findAddons

    const wrapupContent = wrapUpDataFromCombinedQuery?.wrapUps || [];
    // pim data; old query findWrapUpsByKey

    const addOnHoverInfo = addOnDataFromCombinedQuery?.content?.entries || [];
    // cms data; old query findContent, all entries of content-type

    const wrapUpInfo = wrapUpDataFromCombinedQuery?.content?.entries?.[0]?.wrap_up || [];
    const wrapUpHeaderInfo = wrapUpDataFromCombinedQuery?.content?.entries?.[0];

    // cms data; old query findContent, single entry.wrap_up
    // assumes 'Default' titled entry is first in entries list, check brand stacks in CMS

    const wrapUpNBCUEntry = wrapUpDataFromCombinedQuery?.content?.entries?.filter((entry) => entry?.title === 'nbcu wrap up');
    // primarily for 18f
    const movieData = wrapUpNBCUEntry?.map((field) => (field?.wrap_up)) || [];
    // cms data; nbcu titled entry.wrap_up

    if ((lastAddedOrderItem && Object.keys(lastAddedOrderItem).length !== 0)
            && (content && content?.length === 0)
            && (wrapupContent && wrapupContent?.length === 0)) {
        redirectToShipping();
        return null;
    }

    return (
        <>
            <GraphqlSeoHeadTags
                pageType="add-ons"
                seoData={{
                    enable_index_follow: false, page_title: document?.title || 'Add Ons', meta_description: document?.title || 'Add Ons', keywords: 'Add Ons', og: [], seo_searchable: false, canonicalUrl: '/add-ons', canonical_url: '/add-ons', canonical: '',
                }}
                location={{
                    pathname: '/add-ons', search: '',
                }}
            />
            <AddOnsPageBuilder
                content={content}
                wrapupContent={wrapupContent}
                addOnHoverInfo={addOnHoverInfo}
                wrapUpInfo={wrapUpInfo}
                addOnData={movieData}
                sympathyValue={sympathyValue}
                showWrapupProductsOnAddOnPage={location?.state?.showWrapup}
                wrapupAddonRedesign={wrapupAddonRedesign}
                wrapUpHeaderInfo={wrapUpHeaderInfo}
            />
        </>
    );
};

AddOnsContentContainer.propTypes = {
    page: object.isRequired,
    isABFallback: bool,
};

AddOnsContentContainer.defaultProps = {
    isABFallback: false,
};

export default AddOnsContentContainer;
